// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyInteraction, TyInterest, TyLangValue, TyLead, TyLeadReservation, TyLeadStage, TyNote } from "@/types";
import { dueState, formatDate, formatMoney, leadTitle, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import AvatarGroup from "@/UI/atoms/AvatarGroup";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import InteractionCard from "@/UI/cards/InteractionCard";
import InterestCard from "@/UI/cards/InterestCard";
import NoteCard from "@/UI/cards/NoteCard";
import ActionsBar from "@/UI/components/ActionsBar";
import EmptyState from "@/UI/components/EmptyState";
import LeadStepper from "@/UI/components/LeadStepper";
import RecordPanel from "@/UI/components/RecordPanel";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import CloseDealPopup from "@/UI/popups/CloseDealPopup";
import CloseLeadPopup from "@/UI/popups/CloseLeadPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import HistoryPopup from "@/UI/popups/HistoryPopup";
import InteractionFormPopup from "@/UI/popups/InteractionFormPopup";
import InteractionPopup from "@/UI/popups/InteractionPopup";
import InterestFormPopup from "@/UI/popups/InterestFormPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import LeadStagePopup from "@/UI/popups/LeadStagePopup";
import NoteFormPopup from "@/UI/popups/NoteFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/** What the person is across the whole company, which the lead itself cannot say. */
type TyActivity = { leads: number; openLeads: number; interactions: number; ownedUnits: string[] };
type TyResponse = { lead: TyLead; interactions: TyInteraction[]; reservations: TyLeadReservation[]; activity: TyActivity };
type TyTab = "timeline" | "interests" | "details" | "notes";

/**
 * One deal.
 *
 * **The timeline is the screen**, not a tab buried behind the details, because a sales agent opens
 * a lead to answer one of two questions: what did we last say, and what do I owe. Both are the same
 * list — a follow-up is an entry dated ahead — so what is still owed runs full width above the grid
 * of what already happened, and logging a contact is the bar's one add button.
 *
 * **The stage is the trail, not a chip.** A chip reported one word and said nothing about the
 * distance left, which is what a pipeline is read for; the stepper shows the whole path and lets a
 * legal move be made by pressing it. Illegal ones are dead rather than refused — see `LeadStepper`.
 *
 * The header carries one menu instead of a trash can. Closing, reopening, marking a loss, deleting
 * and walking over to the customer are five rare things, and putting the destructive one on the
 * header while the other four hid in the bar got the weighting exactly backwards.
 */
export default function LeadScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { id } = useParams();
    const navigate = useNavigate();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);

    const [params, setParams] = useSearchParams();

    /**
     * The tab is in the URL and **replaces** rather than pushes. A reload, a back button, or a link
     * sent to a colleague all land on the tab that was being looked at — and moving between tabs
     * never stacks history somebody has to press back through to leave the record.
     */
    const tab = (params.get("tab") as TyTab) || "timeline";
    const setTab = (key: TyTab) => setParams(new URLSearchParams(key === "timeline" ? {} : { tab: key }), { replace: true });
    /** Set only by the delete action, so a failed delete leaves you on the record instead of the list. */
    const [isLeaving, setIsLeaving] = useState(false);

    const canManage = isOwner || role === "manager";
    const lead = useFetch<TyResponse>(`/crm/leads/${id ?? "x"}`, {}, false);
    const action = useFetch("/crm/leads", {}, false);

    const load = () => lead.invoke({ path: `/crm/leads/${id}` });

    useEffect(() => {
        if (!id) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!action.succeededAt) return;
        if (isLeaving) {
            navigate("/crm/leads");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const record = lead.data?.lead;
    const interactions = lead.data?.interactions ?? [];
    const reservations = lead.data?.reservations ?? [];
    const activity = lead.data?.activity ?? { leads: 0, openLeads: 0, interactions: 0, ownedUnits: [] };

    if (lead.loading && !record) return <p className="lead-screen__state">{t.common.loading}</p>;
    if (lead.error) return <p className="lead-screen__state lead-screen__state--error">{lead.error}</p>;
    if (!record) return <EmptyState icon="crm" title={t.crm.leadNotFound} description={t.crm.leadNotFoundHint} />;

    const customer = typeof record.customer === "object" ? record.customer : null;
    const title = leadTitle(record, t, (value) => readLangValue(value as TyLangValue[], lang));
    const isFrozen = !!record.closed;
    const isFinished = (constants.leadTerminalStages as readonly string[]).includes(record.stage);

    /** Owed above, done below — the same list cut by whether its entries have an outcome yet. */
    const booked = interactions.filter((entry) => dueState(entry) !== "done").sort((a, b) => +new Date(a.date) - +new Date(b.date));
    const happened = interactions.filter((entry) => dueState(entry) === "done");

    const holders = record.assignees
        .map((entry) => (typeof entry.client === "object" ? { _id: entry.client._id, name: entry.client.name } : null))
        .filter((entry): entry is { _id: string; name: string } => !!entry);

    /**
     * Rows written before the field existed come back without it. `lean()` does not apply schema
     * defaults, so the array is missing rather than empty, and the type saying otherwise is exactly
     * how a screen takes itself down on a record nobody has touched since the migration.
     */
    const interests = record.interests ?? [];

    // --- Actions ---------------------------------------------------------------------------------

    const openInteraction = (interaction?: TyInteraction, planning?: boolean) =>
        addComp({
            // Centred: one short form for both halves of the same record.
            name: "interaction-form",
            Component: InteractionFormPopup,
            anim: "scale",
            details: { leadId: record._id, interaction: interaction ?? null, planning, onDone: load },
        });

    /**
     * The card's own gesture: the contact, read whole. Editing and deleting hang off it rather than
     * off the row, because a timeline is scanned and a row carrying two buttons is a row that reads
     * as a control panel.
     */
    const openInteractionDetails = (interaction: TyInteraction) =>
        addComp({
            name: "interaction",
            Component: InteractionPopup,
            anim: "scale",
            details: {
                interaction,
                // Its own button reads "record what happened" while it is still planned, so it has
                // to land on the side where that can be written rather than on the booking side.
                onEdit: isFrozen ? undefined : () => openInteraction(interaction, dueState(interaction) === "done" ? undefined : false),
                onRemove: isFrozen
                    ? undefined
                    : () =>
                          confirmRemove(t.common.remove, t.crm.confirmRemoveInteraction, () =>
                              action.invoke({ method: "DELETE", path: `/crm/interactions/${interaction._id}` })
                          ),
            },
        });

    const openInterest = (interest?: TyInterest) =>
        addComp({
            name: "interest-form",
            Component: InterestFormPopup,
            anim: "scale",
            details: { leadId: record._id, interest: interest ?? null, onDone: load },
        });

    const openNote = (note?: TyNote) =>
        addComp({
            name: "note-form",
            Component: NoteFormPopup,
            anim: "scale",
            details: { basePath: `/crm/leads/${record._id}`, note: note ?? null, onDone: load },
        });

    const confirmRemove = (title: string, message: string, onConfirm: () => void) =>
        addComp({ name: "confirm", Component: ConfirmPopup, anim: "scale", details: { title, message, confirmLabel: t.common.remove, onConfirm } });

    /**
     * A stage the stepper offered is already legal, with two exceptions that open a form instead:
     * a loss has to say why, and `won` is not a stage anybody types — it is what a reservation or
     * a contract leaves behind, so pressing it asks which of the two happened.
     */
    const moveStage = (stage: TyLeadStage) => {
        if (stage === "lost") {
            addComp({ name: "lead-stage", Component: LeadStagePopup, place: "end", anim: "endSlide", details: { lead: record, onDone: load } });
            return;
        }
        if (stage === "won") {
            addComp({ name: "close-deal", Component: CloseDealPopup, anim: "scale", details: { lead: record, onDone: load } });
            return;
        }
        action.invoke({ method: "PATCH", path: `/crm/leads/${record._id}/stage`, data: { stage } });
    };

    const openMore = (anchor: DOMRect) => {
        const actions: TyAction[] = [];

        // Moving a stage is not here any more: the trail above does it, on a tap, in every tab.
        // Only a loss is left, because it is the one move that has to ask why.
        if (!isFrozen && !isFinished) {
            actions.push({
                key: "lost",
                label: t.crm.stages.lost,
                icon: "clear",
                onSelect: () =>
                    addComp({
                        name: "lead-stage",
                        Component: LeadStagePopup,
                        place: "end",
                        anim: "endSlide",
                        details: { lead: record, onDone: load },
                    }),
            });
        }

        if ((record.stage === "new" && !isFrozen) || (isFrozen && canManage)) {
            actions.push({
                key: "closure",
                label: isFrozen ? t.crm.reopenLead : t.crm.closeLead,
                icon: "clear",
                onSelect: () =>
                    addComp({
                        name: "close-lead",
                        Component: CloseLeadPopup,
                        place: "end",
                        anim: "endSlide",
                        details: { lead: record, onDone: load },
                    }),
            });
        }

        if (customer)
            actions.push({ key: "customer", label: t.crm.customers, icon: "crm", onSelect: () => navigate(`/crm/customers/${customer._id}`) });

        if (canManage) {
            actions.push({
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    confirmRemove(t.common.remove, t.crm.confirmRemoveLead.replace("{name}", title), () => {
                        setIsLeaving(true);
                        action.invoke({ method: "DELETE", path: `/crm/leads/${record._id}` });
                    }),
            });
        }

        addComp({
            name: "lead-more",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    // --- Details ---------------------------------------------------------------------------------

    const budget =
        record.budgetMin || record.budgetMax
            ? [record.budgetMin ? formatMoney(record.budgetMin) : null, record.budgetMax ? formatMoney(record.budgetMax) : null]
                  .filter(Boolean)
                  .join(" – ")
            : null;

    /** Only what somebody filled in — a grid of em-dashes is a page that says nothing. */
    const facts: { key: string; label: string; value: string; ltr?: boolean }[] = [
        { key: "source", label: t.crm.fields.source, value: record.source ? t.crm.sources[record.source] : "" },
        { key: "financing", label: t.crm.fields.financing, value: record.financing ? t.crm.financing[record.financing] : "" },
        { key: "budget", label: t.crm.budget, value: budget ?? "", ltr: true },
        { key: "lastContact", label: t.crm.lastContact, value: record.lastContactedAt ? formatDate(record.lastContactedAt, lang) : "" },
        { key: "interactions", label: t.crm.counting.interactions, value: String(record.interactionsCount) },
        { key: "lostReason", label: t.crm.fields.lostReason, value: record.lostReason ? t.crm.lostReasons[record.lostReason] : "" },
        { key: "closeReason", label: t.crm.fields.closeReason, value: record.closed ? t.crm.closeReasons[record.closed] : "" },
    ].filter((fact) => fact.value);

    const tabs = [
        { key: "timeline", label: t.crm.tabs.timeline },
        { key: "interests", label: t.crm.interestsTitle },
        { key: "details", label: t.crm.tabs.details },
        { key: "notes", label: t.crm.tabs.notes },
    ];

    /** One add button, doing whatever the tab is for — the same place on every screen in the app. */
    const onAdd =
        isFrozen || tab === "details"
            ? undefined
            : tab === "interests"
              ? () => openInterest()
              : tab === "notes"
                ? () => openNote()
                : () => openInteraction();

    return (
        <section className="lead-screen">
            <RecordPanel
                context={customer ? <Link to={`/crm/customers/${customer._id}`}>{customer.name}</Link> : undefined}
                title={title}
                subtitle={
                    customer?.phone ? (
                        // A number reads left to right in every language, so it carries its own
                        // direction rather than inheriting the page's and swapping its own ends.
                        <bdi dir="ltr">{customer.phone}</bdi>
                    ) : undefined
                }
                chips={
                    // No stage here: the trail below says it, in every tab, with the distance left
                    // as well as the name. Two controls reporting one value is one too many.
                    <>
                        {record.closed && <StatusChip tone="neutral" label={t.crm.closedBadge} />}
                        {customer?.isBlocked && <StatusChip tone="danger" label={t.crm.blocked} />}
                    </>
                }
                onHistory={
                    canManage
                        ? () =>
                              addComp({
                                  name: "history",
                                  Component: HistoryPopup,
                                  place: "end",
                                  anim: "endSlide",
                                  details: { title, path: `/crm/leads/${record._id}/updates` },
                              })
                        : undefined
                }
                onEdit={
                    isFrozen
                        ? undefined
                        : () =>
                              addComp({
                                  name: "lead-form",
                                  Component: LeadFormPopup,
                                  place: "end",
                                  anim: "endSlide",
                                  details: { lead: record, onDone: load },
                              })
                }
                onMore={openMore}
            >
                <ActionsBar tabs={tabs} activeTab={tab} onTab={(key) => setTab(key as TyTab)} addLabel={t.common.add} onAdd={onAdd} />
            </RecordPanel>

            {/* The whole path, above everything it explains. */}
            <div className="lead-screen__stage">
                <LeadStepper lead={record} onPick={moveStage} disabled={action.loading} />
            </div>

            {action.error && <p className="lead-screen__state lead-screen__state--error">{action.error}</p>}

            {tab === "timeline" && (
                <div className="lead-screen__grid">
                    {/* What is owed comes first, in the same grid as the rest: it is the one thing on
                        this screen somebody is accountable for, and a pipeline rots by leaving deals
                        alone rather than by losing them. The rail on its edge is what marks it. */}
                    {booked.map((entry) => (
                        <InteractionCard
                            key={entry._id}
                            interaction={entry}
                            onOpen={() => openInteractionDetails(entry)}
                            onLog={isFrozen ? undefined : () => openInteraction(entry, false)}
                        />
                    ))}

                    {!interactions.length && (
                        <EmptyState
                            span
                            icon="phone"
                            title={t.crm.emptyInteractions}
                            description={t.crm.emptyInteractionsHint}
                            actionLabel={t.crm.addInteraction}
                            onAction={() => openInteraction()}
                        />
                    )}

                    {happened.map((entry) => (
                        <InteractionCard key={entry._id} interaction={entry} onOpen={() => openInteractionDetails(entry)} />
                    ))}
                </div>
            )}

            {tab === "interests" && (
                <div className="lead-screen__grid">
                    {!interests.length && (
                        <EmptyState
                            span
                            icon="properties"
                            title={t.crm.emptyInterests}
                            description={t.crm.emptyInterestsHint}
                            actionLabel={t.crm.addInterest}
                            onAction={() => openInterest()}
                        />
                    )}
                    {interests.map((interest) => (
                        <InterestCard
                            key={interest._id}
                            interest={interest}
                            onOpen={isFrozen ? undefined : () => openInterest(interest)}
                            onRemove={
                                isFrozen
                                    ? undefined
                                    : () =>
                                          confirmRemove(t.common.remove, t.crm.confirmRemoveInterest, () =>
                                              action.invoke({ method: "DELETE", path: `/crm/leads/${record._id}/interests/${interest._id}` })
                                          )
                            }
                        />
                    ))}
                </div>
            )}

            {tab === "details" && (
                <div className="lead-screen__details">
                    {/* The person first, and as a person: a face, a number you can ring, and what
                        they are to this company — four figures the lead itself cannot answer. A
                        deal is opened cold far more often than it is opened by whoever filed it,
                        and "who am I about to call" is the question that gets asked first. */}
                    {customer && (
                        <section className="lead-screen__card lead-screen__card--wide">
                            <div className="lead-screen__person">
                                <Avatar name={customer.name} picture={customer.picture ?? null} size={52} rounded bordered />

                                <div className="lead-screen__person-text">
                                    <Link className="lead-screen__person-name" to={`/crm/customers/${customer._id}`}>
                                        {customer.name}
                                        <Icon name="eye" size={14} />
                                    </Link>
                                    <bdi className="lead-screen__person-phone" dir="ltr">
                                        {customer.phone}
                                    </bdi>
                                    {customer.email && <span className="lead-screen__person-mail">{customer.email}</span>}
                                </div>

                                <div className="lead-screen__person-chips">
                                    <StatusChip
                                        size="sm"
                                        tone={customer.status === "client" ? "available" : "accent"}
                                        label={t.crm.statuses[customer.status]}
                                    />
                                    {customer.isBlocked && <StatusChip size="sm" tone="danger" label={t.crm.blocked} />}
                                </div>
                            </div>

                            <dl className="lead-screen__tally">
                                <div className="lead-screen__tally-item">
                                    <dt>{t.crm.interests}</dt>
                                    <dd>{activity.leads}</dd>
                                </div>
                                <div className="lead-screen__tally-item">
                                    <dt>{t.crm.due.open}</dt>
                                    <dd>{activity.openLeads}</dd>
                                </div>
                                <div className="lead-screen__tally-item">
                                    <dt>{t.crm.counting.interactions}</dt>
                                    <dd>{activity.interactions}</dd>
                                </div>
                                <div className="lead-screen__tally-item">
                                    <dt>{t.crm.ownsUnit}</dt>
                                    <dd>{activity.ownedUnits.length ? activity.ownedUnits.join("، ") : t.crm.notSet}</dd>
                                </div>
                            </dl>
                        </section>
                    )}

                    {/* Who holds it, as faces. Each one answers itself when it is pressed. */}
                    <section className="lead-screen__card">
                        <h2 className="lead-screen__card-title">{t.crm.fields.assignees}</h2>
                        <div className="lead-screen__holders">
                            <AvatarGroup people={holders} size={36} max={6} empty={t.crm.unassigned} />
                            {holders.length > 0 && (
                                <span className="lead-screen__holder-names">{holders.map((person) => person.name).join("، ")}</span>
                            )}
                        </div>
                    </section>

                    {facts.length > 0 && (
                        <section className="lead-screen__card">
                            <h2 className="lead-screen__card-title">{t.crm.tabs.details}</h2>
                            <dl className="lead-screen__facts">
                                {facts.map((fact) => (
                                    <div key={fact.key} className="lead-screen__fact">
                                        <dt>{fact.label}</dt>
                                        <dd>{fact.ltr ? <bdi dir="ltr">{fact.value}</bdi> : fact.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    )}

                    {/* A lead holds no back-link to its reservation — most reach nothing. Read the
                        other way round, which is what the reservation's own index is for. */}
                    <section className="lead-screen__card">
                        <h2 className="lead-screen__card-title">{t.crm.reservations}</h2>
                        {!reservations.length && <EmptyState icon="history" title={t.crm.emptyReservations} />}
                        {reservations.map((reservation) => (
                            <p key={reservation._id} className="lead-screen__reservation">
                                {typeof reservation.unit === "object" && reservation.unit ? reservation.unit.number : ""} · {reservation.status}
                            </p>
                        ))}
                    </section>
                </div>
            )}

            {tab === "notes" && (
                <div className="lead-screen__grid">
                    {!(record.notes ?? []).length && (
                        <EmptyState span icon="edit" title={t.crm.emptyNotes} actionLabel={t.crm.addNote} onAction={() => openNote()} />
                    )}
                    {(record.notes ?? []).map((entry) => (
                        <NoteCard
                            key={entry._id}
                            note={entry}
                            onOpen={isFrozen ? undefined : () => openNote(entry)}
                            onRemove={
                                isFrozen
                                    ? undefined
                                    : () =>
                                          confirmRemove(t.common.remove, t.crm.confirmRemoveNote, () =>
                                              action.invoke({ method: "DELETE", path: `/crm/leads/${record._id}/notes/${entry._id}` })
                                          )
                            }
                        />
                    ))}
                </div>
            )}
        </section>
    );
}
